import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { useRef } from "react";
import { stripInvalidPropsForElement } from "../../../utils/component-props";
import { InputDescription } from "../InputDescription";
import { InputLabel } from "../InputLabel";
import { InvalidInputMessage } from "../InvalidInputMessage";
import { InvalidInputMessageTooltip } from "../InvalidInputMessageTooltip";
import styles from "./InputWithLabels.module.scss";
import type { InputWithLabelsProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Adorns an input with a label, description, and conditionally rendered invalid
 * messaging, if the input is invalid.
 */
export const InputWithLabels = ({
  children,
  label,
  isLabelShown = true,
  labelPosition = "outside",
  description,
  isRequired,
  isDisabled,
  isLoading,
  isBusy,
  isOptionalTextShown = true,
  shouldShowDescriptionInTooltip = true,
  isInline,
  isFluid,
  invalidMessage,
  colorScheme = "light",
  className,
  style,
  ...restProps
}: InputWithLabelsProps) => {
  const labelId = useRef(nanoid(6));
  const inputId = useRef(nanoid(6));
  const invalidMessageId = useRef(nanoid(6));

  return (
    <div
      {...stripInvalidPropsForElement(restProps, "div")}
      className={cx(
        "container",
        { "container--inline": isInline, "container--fluid": isFluid },
        className
      )}
      style={style}
    >
      {label && isLabelShown && labelPosition === "outside" ? (
        <InputLabel
          colorScheme="light"
          isLoading={isLoading}
          isBusy={isBusy}
          id={labelId.current}
          inputId={inputId.current}
          isRequired={isRequired}
          isDisabled={isDisabled}
          description={description}
          isOptionalTextShown={isOptionalTextShown}
          shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
        >
          {label}
        </InputLabel>
      ) : null}

      {typeof children === "function"
        ? children({
            labelId: labelId.current,
            inputId: inputId.current,
            leftSlot:
              isLabelShown && labelPosition === "inside"
                ? ({ className, style }) => (
                    <InputLabel
                      colorScheme="light"
                      isLoading={isLoading}
                      isBusy={isBusy}
                      id={labelId.current}
                      inputId={inputId.current}
                      isRequired={isRequired}
                      isDisabled={isDisabled}
                      description={description}
                      isOptionalTextShown={false}
                      shouldShowDescriptionInTooltip={
                        shouldShowDescriptionInTooltip
                      }
                      style={{ marginBottom: 0, ...style }}
                      className={className}
                    >
                      {label}
                    </InputLabel>
                  )
                : !isLabelShown && description
                  ? ({ className, style }) => (
                      <InputDescription
                        colorScheme="light"
                        isDisabled={isDisabled}
                        isLoading={isLoading}
                        isBusy={isBusy}
                        iconProps={{
                          "aria-roledescription": "More information",
                        }}
                        aria-label="More information about this input"
                        id={labelId.current}
                        className={className}
                        style={style}
                      >
                        {description}
                      </InputDescription>
                    )
                  : null,
            flairSlot:
              isInline && invalidMessage
                ? ({ className, style }) => (
                    <InvalidInputMessageTooltip
                      intent="error"
                      iconProps={{ size: 12 }}
                      id={invalidMessageId.current}
                      className={className}
                      style={style}
                    >
                      {invalidMessage}
                    </InvalidInputMessageTooltip>
                  )
                : null,
            invalidMessageId: invalidMessage
              ? invalidMessageId.current
              : undefined,
          })
        : children}

      {!isInline && invalidMessage ? (
        <InvalidInputMessage
          id={invalidMessageId.current}
          intent="error"
          isDisabled={isDisabled}
          isLoading={isLoading}
          isBusy={isBusy}
          className={cx("error-message")}
        >
          {invalidMessage}
        </InvalidInputMessage>
      ) : null}
    </div>
  );
};
